import React from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { getSignedMediaUrl } from '@/lib/utils';
import { MobileHamburgerMenu } from './MobileHamburgerMenu';

interface MobileHeroSectionProps {
  event: {
    id?: string;
    name: string;
    description: string;
    card_image_id?: string | null;
    base_image_id?: string | null;
  };
  business?: {
    handle?: string;
    name: string;
    location?: {
      city: string;
    };
    card_image_id?: string | null;
    base_image_id?: string | null;
  } | null;
  bookingCategory?: {
    base_image_id?: string | null;
    card_image_id?: string | null;
  } | null;
  isAuthenticated?: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  onBack?: () => void;
}

export const MobileHeroSection: React.FC<MobileHeroSectionProps> = ({ event, business, bookingCategory, isAuthenticated, scrollRef, onBack }) => {
  const navigate = useNavigate();
  
  // Track scrolling relative to the parent container setup
  const { scrollY } = useScroll({ container: scrollRef });

  // Create fade out for the center text (opacity mapped 1 to 0 over scroll pixels 50->150)
  const heroTextOpacity = useTransform(scrollY, [50, 150], [1, 0]);
  const heroTextY = useTransform(scrollY, [0, 150], [0, -30]);

  // Create sliding app bar transition (opacity mapped 0 to 1 over scroll pixels)
  // Show app bar background incrementally, and show text exactly as hero text disappears
  const appBarBgOpacity = useTransform(scrollY, [50, 150], [0, 1]);
  const appBarTextOpacity = useTransform(scrollY, [130, 180], [0, 1]);

  // Parallax the background image specifically
  const bgY = useTransform(scrollY, [0, 400], [0, 120]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (business?.handle && event?.id) {
      navigate(`/venues/${business.handle}/events/${event.id}`);
    } else {
      navigate(-1);
    }
  };

  // Get the event media URL and type
  const getEventMedia = () => {
    const activeBookingCatId = bookingCategory?.base_image_id || bookingCategory?.card_image_id;
    if (activeBookingCatId && activeBookingCatId !== '00000000-0000-0000-0000-000000000000') {
      const isVideo = activeBookingCatId.includes('video') || activeBookingCatId.includes('mp4') || activeBookingCatId.includes('webm');
      return {
        url: getSignedMediaUrl(activeBookingCatId, isVideo ? 'video' : 'image').url,
        isVideo
      };
    }
    
    const activeEventImageId = event?.base_image_id || event?.card_image_id;
    if (activeEventImageId && activeEventImageId !== '00000000-0000-0000-0000-000000000000') {
      const isVideo = activeEventImageId.includes('video') || activeEventImageId.includes('mp4') || activeEventImageId.includes('webm');
      return {
        url: getSignedMediaUrl(activeEventImageId, isVideo ? 'video' : 'image').url,
        isVideo
      };
    }
    const activeBusinessImageId = business?.base_image_id || business?.card_image_id;
    if (activeBusinessImageId && activeBusinessImageId !== '00000000-0000-0000-0000-000000000000') {
      const isVideo = activeBusinessImageId.includes('video') || activeBusinessImageId.includes('mp4') || activeBusinessImageId.includes('webm');
      return {
        url: getSignedMediaUrl(activeBusinessImageId, isVideo ? 'video' : 'image').url,
        isVideo
      };
    }
    return null;
  };

  const eventMedia = getEventMedia();

  return (
    <>
      {/* 1. Fixed App Bar */}
      <motion.div 
        className="fixed top-0 left-0 right-0 z-50 pt-[env(safe-area-inset-top,0px)]"
        style={{
          // Use framer motion variants to directly map rgba strings
          backgroundColor: useTransform(
            appBarBgOpacity, 
            opacity => `rgba(10, 10, 10, ${opacity * 0.95})`
          ),
          backdropFilter: useTransform(
            appBarBgOpacity,
            opacity => `blur(${opacity * 20}px)`
          ),
          WebkitBackdropFilter: useTransform(
            appBarBgOpacity,
            opacity => `blur(${opacity * 20}px)`
          ),
          // Slight bottom border fade in
          borderBottom: useTransform(
            appBarBgOpacity,
            opacity => `1px solid rgba(255,255,255, ${opacity * 0.1})`
          )
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 to-transparent pointer-events-none opacity-80" />
        <div className="px-4 h-16 flex items-center justify-between relative z-10">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            aria-label="Go back"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>

          {/* Squeezed Event Title (Hidden until scrolled) */}
          <motion.div 
            style={{ opacity: appBarTextOpacity }}
            className="flex-1 px-4 text-center overflow-hidden"
          >
            <h2 className="text-lg font-serif font-bold text-white truncate max-w-full tracking-tight">
              {event?.name || 'Event'}
            </h2>
          </motion.div>

          {/* Empty Placeholder to balance the flex container so the text stays centered */}
          <div className="w-10"></div>
        </div>
      </motion.div>

      {/* Mobile Hamburger Menu Container */}
      <div className="fixed top-0 right-0 h-[calc(4rem+env(safe-area-inset-top,0px))] pt-[env(safe-area-inset-top,0px)] px-4 flex items-center justify-end z-[100] pointer-events-none">
        <div className="pointer-events-auto">
          <MobileHamburgerMenu clubHandle={business?.handle || null} isAuthenticated={isAuthenticated} />
        </div>
      </div>

      {/* 2. Scrollable Hero Region (reduced height) */}
      <div 
        className="relative w-full overflow-hidden shadow-2xl z-10 bg-gray-900"
        style={{ paddingBottom: '40%' }} // Reduced aspect ratio for shorter hero
      >
        
        {/* Absolute Background Wrapper that parallax scrolls */}
        <motion.div 
          style={{ y: bgY }}
           className="absolute inset-x-0 -inset-y-32 w-full flex items-center justify-center -z-10"
        >
          {eventMedia ? (
            eventMedia.isVideo ? (
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              >
                <source src={eventMedia.url} type="video/mp4" />
                <source src={eventMedia.url} type="video/webm" />
              </video>
            ) : (
              <img
                src={eventMedia.url}
                alt={event.name}
                className="w-full h-full object-cover"
              />
            )
          ) : (
             <div 
              className={`w-full h-full bg-cover bg-center`}
              style={{ 
                backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            />
          )}
        </motion.div>

        {/* Heavy Gradient Overlay over the background so text pops, anchored to the hero section bounds */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20 pointer-events-none" />

        {/* Centered Content -> Fades out on scroll */}
        <motion.div 
          style={{
             opacity: heroTextOpacity,
             y: heroTextY
          }}
          className="absolute inset-x-0 bottom-0 top-0 flex flex-col items-center justify-center px-6 text-center z-20"
        >
          <div className="space-y-1.5 flex flex-col items-center max-w-sm">
            {/* Location */}
            {business?.name && (
              <span className="text-xs font-semibold text-blue-400 uppercase tracking-widest drop-shadow-md">
                {business.location?.city ? `${business.name}, ${business.location.city}` : business.name}
              </span>
            )}
            
            {/* Event Name */}
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-white tracking-tight drop-shadow-lg leading-tight line-clamp-2 pb-1">
              {event?.name || 'Event'}
            </h1>
            
            {/* Description / Date removed per design constraints */}
          </div>
        </motion.div>
      </div>
    </>
  );
};
